import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrendingUp, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import useRevealOnScroll from '../hooks/useRevealOnScroll';
import './BoostPage.css';

const BENEFIT_ICONS = [TrendingUp, Star];

const BOOST_PACKAGES = [
  { key: 'boost_single', price: '€10.99' },
];

const PACKAGE_KEY = 'boost_pkg';

function readStoredPackage() {
  try {
    const v = sessionStorage.getItem(PACKAGE_KEY);
    if (v && BOOST_PACKAGES.some(p => p.key === v)) return v;
  } catch (_) {/* ignore */}
  return null;
}

export default function BoostPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [benefitsRef, benefitsVisible] = useRevealOnScroll(0.1);
  const cancelled = searchParams.get('cancelled') === '1';

  const [selectedPackage, setSelectedPackage] = useState(() => readStoredPackage());

  // Mirror to sessionStorage so refresh / Stripe-cancel preserves the choice.
  useEffect(() => {
    if (!selectedPackage) return;
    try { sessionStorage.setItem(PACKAGE_KEY, selectedPackage); } catch (_) {}
  }, [selectedPackage]);

  const handleBoostNow = () => {
    if (!selectedPackage) return;
    const next = `/boost-select?package=${selectedPackage}`;
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(next)}`);
      return;
    }
    navigate(next, { state: { package: selectedPackage } });
  };

  const benefits = t('boostPage.benefits') || [];

  return (
    <div className="boostPage">
      <section className="boostHero">
        <div className="boostHero__inner">
          <span className="boostHero__eyebrow">
            <span className="boostHero__eyebrowLine" />
            <span className="boostHero__eyebrowText">{t('boostPage.eyebrow')}</span>
            <span className="boostHero__eyebrowLine" />
          </span>
          <h1 className="boostHero__title">
            {t('boostPage.heroTitle')}
          </h1>
          <p className="boostHero__subtitle">
            {t('boostPage.heroSub')}
          </p>
          {cancelled && (
            <div className="boostHero__cancelled" role="status">
              {t('boostPage.cancelled')}
            </div>
          )}
        </div>
      </section>

      <section className="boostBenefits">
        <div className="boostBenefits__inner">
          <header className="boostBenefits__header">
            <h2 className="boostBenefits__title">{t('boostPage.benefitsTitle')}</h2>
          </header>

          <div className="boostBenefits__grid" ref={benefitsRef}>
            {benefits.map((benefit, idx) => {
              const Icon = BENEFIT_ICONS[idx] || TrendingUp;
              return (
                <article
                  key={idx}
                  className={`boostBenefitCard featureReveal${benefitsVisible ? ' featureReveal--visible' : ''}`}
                  style={benefitsVisible ? { animationDelay: `${idx * 90}ms` } : {}}
                >
                  <div className="boostBenefitCard__iconWrap">
                    <Icon size={24} />
                  </div>
                  <h3 className="boostBenefitCard__title">{benefit.title}</h3>
                  <p className="boostBenefitCard__description">{benefit.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="boostPackages">
        <div className="boostPackages__inner">
          <header className="boostPackages__header">
            <h2 className="boostPackages__title">{t('boostPage.packagesTitle')}</h2>
          </header>
          <div className="boostPackages__grid boostPackages__grid--single">
            {BOOST_PACKAGES.map((pkg) => {
              const isSelected = selectedPackage === pkg.key;
              return (
                <button
                  key={pkg.key}
                  type="button"
                  className={
                    'boostPackages__card' +
                    (isSelected ? ' boostPackages__card--selected' : '')
                  }
                  onClick={() => setSelectedPackage(pkg.key)}
                  aria-pressed={isSelected}
                >
                  <div className="boostPackages__label">{t('boostPage.packageListingLabel')}</div>
                  <div className="boostPackages__priceBlock">
                    <div className="boostPackages__price">{pkg.price}</div>
                    <div className="boostPackages__subtitle">{t('boostPage.packageDuration')}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="boostPackages__ctaRow">
            <button
              type="button"
              className="boostPackages__cta"
              onClick={handleBoostNow}
              disabled={!selectedPackage}
            >
              {t('boostPage.cta')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
